import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { FileSpreadsheet, Download, Loader2 } from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import type { I18nKey } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { changes } from '@/lib/store-adapter';
import {
  latestReports,
  readReportDocs,
  ReportFormat,
  ReportStatus,
  type ReportRow,
} from '@/lib/reports';
import { fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollTable } from '@/components/layout';

const REPORT_TYPES = [
  'customer_summary',
  'extinguisher_due',
  'tenant_activity',
] as const;

const TYPE_LABEL: Record<(typeof REPORT_TYPES)[number], I18nKey> = {
  customer_summary: 'reports.type.customer_summary',
  extinguisher_due: 'reports.type.extinguisher_due',
  tenant_activity: 'reports.type.tenant_activity',
};

const STATUS_LABEL: Record<ReportStatus, I18nKey> = {
  [ReportStatus.QUEUED]: 'reports.status.queued',
  [ReportStatus.DONE]: 'reports.status.done',
  [ReportStatus.FAILED]: 'reports.status.failed',
};

export function Reports() {
  const t = useT();
  const toast = useApp(s => s.toast);
  const [reportType, setReportType] =
    useState<(typeof REPORT_TYPES)[number]>('customer_summary');
  const [format, setFormat] = useState<ReportFormat>(ReportFormat.CSV);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<ReportRow[]>([]);

  const refresh = useCallback(async () => {
    try {
      setRows(latestReports(await readReportDocs()));
    } catch {
      // local DB unavailable (e.g. logged out) — keep whatever we had
    }
  }, []);

  useEffect(() => {
    void refresh();
    // report/report_request docs replicate in asynchronously — stay live.
    return changes(['report', 'report_request'], () => void refresh());
  }, [refresh]);

  const generate = async () => {
    setBusy(true);
    try {
      await apiFetch(`/reports/${reportType}`, {
        method: 'POST',
        body: JSON.stringify({ format }),
      });
      toast(t('reports.requested'));
      void refresh();
    } catch {
      toast(t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-primary" />
          {t('reports.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('reports.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('reports.generate')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-64">
              <Label>{t('reports.reportType')}</Label>
              <Select
                value={reportType}
                onValueChange={v =>
                  setReportType(v as (typeof REPORT_TYPES)[number])
                }
              >
                <SelectTrigger data-testid="report-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(rt => (
                    <SelectItem key={rt} value={rt}>
                      {t(TYPE_LABEL[rt])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('reports.format')}</Label>
              <Tabs
                value={format}
                onValueChange={v => setFormat(v as ReportFormat)}
              >
                <TabsList>
                  <TabsTrigger value={ReportFormat.CSV}>
                    CSV {/* i18n-ignore */}
                  </TabsTrigger>
                  <TabsTrigger value={ReportFormat.PDF}>
                    PDF {/* i18n-ignore */}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <Button onClick={generate} disabled={busy} data-testid="report-go">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('reports.generateButton')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('reports.history')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollTable minWidth={520}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('calendar.date')}</TableHead>
                  <TableHead>{t('reports.reportType')}</TableHead>
                  <TableHead>{t('reports.format')}</TableHead>
                  <TableHead>{t('table.status')}</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(r.requestedAt.slice(0, 10))}
                    </TableCell>
                    <TableCell className="font-medium">
                      {TYPE_LABEL[r.reportType as keyof typeof TYPE_LABEL]
                        ? t(TYPE_LABEL[r.reportType as keyof typeof TYPE_LABEL])
                        : r.reportType}
                    </TableCell>
                    <TableCell className="font-mono text-xs uppercase">
                      {r.format}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.status === ReportStatus.DONE
                            ? 'default'
                            : r.status === ReportStatus.FAILED
                              ? 'destructive'
                              : 'secondary'
                        }
                        title={r.error}
                      >
                        {t(STATUS_LABEL[r.status] ?? 'reports.status.queued')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.downloadUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a
                            href={r.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Download className="h-3 w-3" />
                            {t('reports.download')}
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-6 text-center text-sm text-muted-foreground"
                    >
                      {t('reports.none')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollTable>
        </CardContent>
      </Card>
    </motion.div>
  );
}
